import { useEffect, useState } from 'react';
import {
  BASE_CURRENCY,
  RATE_SOURCE_LABEL,
  RATE_SOURCES,
  type CurrencyRow,
  type RateSource,
} from 'shared';
import { api } from '../api';
import AdminList from '../AdminList';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';
import { alert, haptic } from '../telegram';
import { toast } from '../toast';

/**
 * Валюты площадки.
 *
 * Набор валют — решение о рынках, а не о коде: включить тенге нельзя было
 * без выкатки, а это неправильная цена за управленческое решение. Здесь
 * администратор включает валюты, выбирает, откуда берётся курс, и задаёт
 * его руками там, где автоматики нет или ей не доверяют.
 *
 * Три независимых признака у каждой валюты, и путать их нельзя:
 *
 * - **включена** — участвует в системе вообще;
 * - **сделки** — в ней можно назначить заявку;
 * - **показ** — в ней можно смотреть суммы.
 *
 * Валюта может быть только для показа: смотреть в юанях, не заключая в
 * них сделок, — обычное дело. Поэтому в списке они стоят тремя метками, а
 * не одним состоянием: свести их в «включена» значило бы соврать.
 */

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

const rateText = (c: CurrencyRow) =>
  c.rate === null ? null : `${c.rate.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ₽`;

export default function AdminCurrencies() {
  const [items, setItems] = useState<CurrencyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  /** Черновик ручного курса: пока его набирают, сохранять нечего. */
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.admin.currencies();
      setItems(r.items);
      setDraft(
        Object.fromEntries(
          r.items.map((c) => [c.code, c.manualRate != null ? String(c.manualRate) : '']),
        ),
      );
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function patch(
    code: string,
    data: Parameters<typeof api.admin.updateCurrency>[1],
  ) {
    setBusy(code);
    try {
      await api.admin.updateCurrency(code, data);
      await load();
      toast.success('Сохранено');
    } catch (e) {
      // Отказ здесь всегда содержательный: рубль выключить нельзя, валюта
      // сделки без курса невозможна. Показываем как есть.
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <div className="empty">Загрузка…</div>;

  const open = items.find((c) => c.code === expanded) ?? null;

  const q = query.trim().toLowerCase();
  const shown = items
    .filter((c) => (activeOnly ? c.active : true))
    .filter((c) => (q ? `${c.code} ${c.name}`.toLowerCase().includes(q) : true));

  return (
    <>
      <div className="meta" style={{ marginBottom: 'var(--s3)' }}>
        Курс — сколько рублей стоит одна единица. Рубль остаётся валютой
        учёта: в нём хранятся все суммы площадки, и выключить его нельзя.
      </div>

      <AdminFilters
        active={activeOnly ? 1 : 0}
        search={
          <input
            placeholder="Код или название"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        <button
          type="button"
          className={activeOnly ? 'chip chip--on' : 'chip'}
          onClick={() => {
            haptic();
            setActiveOnly((v) => !v);
          }}
        >
          Только включённые
        </button>
      </AdminFilters>

      <AdminList
        rows={shown}
        keyOf={(c) => c.code}
        empty="Валют не нашлось"
        // Включённая валюта без курса — поломка: в ней уже можно выбрать
        // сделку, а пересчитать её не во что.
        highlight={(c) => c.active && c.rate === null}
        action={{ label: 'Открыть', onClick: (c) => setExpanded(c.code) }}
        columns={[
          {
            title: 'Валюта',
            cell: (c) => (
              <>
                <strong>
                  {c.sign} {c.code}
                </strong>
                <span className="meta" style={{ display: 'block' }}>
                  {c.name}
                </span>
              </>
            ),
          },
          {
            title: 'Курс',
            width: '150px',
            labelOnPhone: true,
            inline: true,
            cell: (c) =>
              rateText(c) ? (
                <span className="tabular">{rateText(c)}</span>
              ) : (
                <span className="badge badge--warn">курса нет</span>
              ),
          },
          {
            title: 'Где участвует',
            width: '210px',
            labelOnPhone: true,
            cell: (c) =>
              c.active ? (
                <>
                  {c.deal && <span className="badge">сделки</span>}
                  {c.display && <span className="badge">показ</span>}
                  {!c.deal && !c.display && <span className="meta">нигде</span>}
                </>
              ) : (
                <span className="meta">выключена</span>
              ),
          },
          {
            title: 'Источник курса',
            width: '190px',
            labelOnPhone: true,
            cell: (c) =>
              c.code === BASE_CURRENCY ? (
                'валюта учёта'
              ) : (
                <>
                  {RATE_SOURCE_LABEL[c.rateSource]}
                  {c.rateSource === 'MANUAL' && c.rateSetAt && (
                    <span className="meta" style={{ display: 'block' }}>
                      задан {when(c.rateSetAt)}
                    </span>
                  )}
                </>
              ),
          },
        ]}
      />

      <AdminSheet
        open={open !== null}
        onClose={() => setExpanded(null)}
        title={open ? `${open.sign} ${open.code}` : ''}
        subtitle={
          open && (
            <>
              {open.name}
              {rateText(open) ? ` · ${rateText(open)}` : ' · курса нет'}
            </>
          )
        }
        actions={
          open && (
            <>
              <button className="secondary" onClick={() => setExpanded(null)}>
                Закрыть
              </button>
              {open.code !== BASE_CURRENCY && open.rateSource === 'MANUAL' && (
                <button
                  disabled={busy === open.code || !draft[open.code]}
                  onClick={() => void patch(open.code, { manualRate: Number(draft[open.code]) })}
                >
                  Сохранить курс
                </button>
              )}
            </>
          )
        }
      >
        {open && (
          <>
            <div className="cur__flags">
              <label className="check">
                <input
                  type="checkbox"
                  disabled={busy === open.code || open.code === BASE_CURRENCY}
                  checked={open.active}
                  onChange={(e) => void patch(open.code, { active: e.target.checked })}
                />
                Включена
              </label>
              <label className="check" title="Можно назначить заявку в этой валюте">
                <input
                  type="checkbox"
                  disabled={busy === open.code || !open.active || open.code === BASE_CURRENCY}
                  checked={open.deal}
                  onChange={(e) => void patch(open.code, { deal: e.target.checked })}
                />
                Сделки
              </label>
              <label className="check" title="Можно смотреть суммы в этой валюте">
                <input
                  type="checkbox"
                  disabled={busy === open.code || !open.active}
                  checked={open.display}
                  onChange={(e) => void patch(open.code, { display: e.target.checked })}
                />
                Показ
              </label>
            </div>

            {open.code !== BASE_CURRENCY && (
              <>
                <div className="divider" />

                <div className="field">
                  <label>Откуда берётся курс</label>
                  <div className="seg">
                    {RATE_SOURCES.map((src) => (
                      <button
                        key={src}
                        type="button"
                        className={
                          open.rateSource === src ? 'seg__btn seg__btn--on' : 'seg__btn'
                        }
                        disabled={busy === open.code}
                        onClick={() => void patch(open.code, { rateSource: src as RateSource })}
                      >
                        {RATE_SOURCE_LABEL[src]}
                      </button>
                    ))}
                  </div>
                </div>

                {open.rateSource === 'MANUAL' && (
                  <div className="field">
                    <label>Рублей за одну единицу</label>
                    <input
                      type="number"
                      step="0.0001"
                      inputMode="decimal"
                      placeholder="₽ за единицу"
                      value={draft[open.code] ?? ''}
                      disabled={busy === open.code}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, [open.code]: e.target.value }))
                      }
                    />
                  </div>
                )}

                {/* Кто и когда правил курс руками — при разборе спора о сумме
                    это первое, что спросят. */}
                {open.rateSetAt && open.rateSource === 'MANUAL' && (
                  <div className="meta">Курс задан вручную {when(open.rateSetAt)}</div>
                )}
                {open.rateSource === 'CBR' && (
                  <div className="meta">Обновляется из ЦБ раз в сутки</div>
                )}
              </>
            )}
          </>
        )}
      </AdminSheet>
    </>
  );
}
